import React, { useEffect, useRef, useState } from "react";
import { GlassCard, FileDropzone } from "../gc/ui";
import { spriteApi, ai as aiApi, projects as projectsApi } from "../../lib/api";
import { SPRITE_SEARCH_INTENTS, SPRITE_SEARCH_STYLES, guessSheetLayout, sliceSpriteSheet, readAsDataUrl } from "../../lib/gamecraft";
import { toast } from "sonner";
import {
  MagnifyingGlass, FloppyDisk, FilmStrip, Sparkle, ArrowSquareOut, Scissors, Play, Pause,
} from "@phosphor-icons/react";

export default function SpriteSearchPanel({ project, onChange, studio, onOpenAnim }) {
  const [q, setQ] = useState("héroe");
  const [kind, setKind] = useState("character");
  const [intent, setIntent] = useState("pixel");
  const [style, setStyle] = useState("any");
  const [hits, setHits] = useState([]);
  const [links, setLinks] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ideas, setIdeas] = useState("");
  const [sheet, setSheet] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const didSearch = useRef(false);

  const kinds = [
    { id: "character", label: "Personaje" },
    ...(studio.kinds || []).filter((k) => k.id !== "character"),
  ];

  useEffect(() => {
    setKind("character");
    setQ("héroe");
    didSearch.current = false;
  }, [studio.id]);

  useEffect(() => {
    if (didSearch.current) return;
    didSearch.current = true;
    search("héroe", "character");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio.id]);

  const search = async (query = q, kindId = kind) => {
    setSearching(true);
    try {
      const data = await spriteApi.search({
        q: (query || "héroe personaje").trim(),
        studio: "creatures",
        kind: kindId || "character",
        intent,
        style,
      });
      setHits(data.results || []);
      setLinks(data.links || []);
      if (!(data.results || []).length) toast.info("Sin resultados CC. Prueba otra palabra o el enlace de OpenGameArt.");
    } catch {
      toast.error("Búsqueda no disponible (¿backend activo?)");
      setHits([]);
    } finally {
      setSearching(false);
    }
  };

  const fetchImage = async (hit) => {
    const url = hit.url || hit.thumbnail;
    if (!url) throw new Error("sin url");
    if (url.startsWith("data:")) return { data_url: url, name: hit.title };
    return spriteApi.fetch(url, hit.title);
  };

  const saveSprite = async (hit, role = "sprite") => {
    try {
      const file = await fetchImage(hit);
      const payload = {
        name: (hit.title || "ref").slice(0, 60),
        data_url: file.data_url,
        category: kind || studio.kinds[0].id,
        studio: studio.id,
        license: hit.license,
        source: hit.foreign_landing_url || hit.url,
        creator: hit.creator,
        role,
      };
      try {
        await projectsApi.addSprite(project.project_id, payload);
        const p = await projectsApi.get(project.project_id);
        if (p) onChange({ sprites: p.sprites });
      } catch {
        onChange({ sprites: [...(project.sprites || []), { id: crypto.randomUUID(), ...payload }] });
      }
      toast.success(role === "sketch" ? "Boceto guardado" : "Sprite guardado");
    } catch {
      if (hit.foreign_landing_url) window.open(hit.foreign_landing_url, "_blank", "noopener");
      toast.info("No se pudo importar directo. Abrí la fuente CC.");
    }
  };

  const openFromDataUrl = async (data_url, title = "sheet", hit = {}) => {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = data_url;
    });
    const layout = guessSheetLayout(img.width, img.height);
    setSheet({
      hit: {
        title,
        license: hit.license || "own",
        creator: hit.creator || "tú",
        foreign_landing_url: hit.foreign_landing_url || "",
      },
      data_url,
      width: img.width,
      height: img.height,
      cols: layout.cols,
      rows: layout.rows,
      name: title.slice(0, 40),
    });
  };

  const openSlicer = async (hit) => {
    try {
      const file = await fetchImage(hit);
      await openFromDataUrl(file.data_url, hit.title || "sheet", hit);
    } catch {
      toast.error("No se pudo abrir el sheet");
    }
  };

  const uploadSheet = async (files) => {
    for (const f of files) {
      const data_url = await readAsDataUrl(f);
      await openFromDataUrl(data_url, f.name.replace(/\.[^.]+$/, ""));
    }
  };

  const askIdeas = async () => {
    try {
      const r = await aiApi.ideas({
        prompt: `Dame 8 ideas visuales (boceto, sprite y frames de animación) para ${studio.label} / ${kind} en un juego 2D ${project.genre || "platformer"} "${project.name}". Query: "${q || "atmosfera"}". Incluye pose, paleta y 1 búsqueda web.`,
        category: "sprites",
        language: "es",
      });
      setIdeas(r.text || "");
    } catch {
      toast.error("IA de ideas no disponible");
    }
  };

  const generateSprite = async () => {
    const prompt = q.trim() || "héroe personaje";
    setGenLoading(true);
    try {
      const res = await aiApi.sprite({
        prompt: `pixel art 2D game character sprite, ${prompt}. Type: ${kind || "character"}. Transparent background, no text, 32x32.`,
        style: "pixel art",
      });
      if (res.needs_api_key) {
        toast.error("Añade tu Google API key en Ajustes para generar sprites.");
        return;
      }
      if (res.source === "procedural" && /character|hero|npc|personaje/i.test(kind || "")) {
        toast.error("Ningún motor de imagen respondió. No importé un placeholder que no es un personaje.");
        return;
      }
      const raw = res.data || "";
      const url = raw.startsWith("data:") ? raw : `data:${res.mime_type || "image/png"};base64,${raw}`;
      await projectsApi.addSprite(project.project_id, {
        name: prompt.slice(0, 40),
        data_url: url,
        category: kind,
        studio: studio.id,
      });
      const p = await projectsApi.get(project.project_id);
      onChange({ sprites: p.sprites });
      toast.success(
        res.source === "gemini" ? "Sprite generado con Gemini"
          : res.source === "pollinations" ? "Sprite generado (motor de imagen de respaldo)"
          : "Gemini no respondió; importé un sprite procedural"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el sprite");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="gc-card p-4">
        <div className="text-sm font-semibold mb-1">Buscar refs, bocetos y sprites CC</div>
        <p className="text-xs mb-3" style={{ color: "var(--gc-muted)" }}>
          Openverse ilustración + pixel (CC0 / CC-BY). Filtra 8-bit / 16-bit / LPC. Importa citando autor; los sheets se cortan en frames.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {SPRITE_SEARCH_INTENTS.map((it) => (
            <button
              key={it.id}
              className={"gc-chip " + (intent === it.id ? "gc-chip-on" : "")}
              onClick={() => setIntent(it.id)}
            >
              {it.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SPRITE_SEARCH_STYLES.map((st) => (
            <button
              key={st.id}
              className={"gc-chip " + (style === st.id ? "gc-chip-on" : "")}
              onClick={() => setStyle(st.id)}
            >
              {st.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {kinds.map((k) => (
            <button
              key={k.id}
              className={"gc-chip " + (kind === k.id ? "gc-chip-on" : "")}
              onClick={() => setKind(k.id)}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { t: "Openverse", u: `https://openverse.org/search/image?q=${encodeURIComponent(q || studio.query || studio.label)}` },
            { t: "OpenGameArt", u: `https://opengameart.org/art-search-advanced?keys=${encodeURIComponent(q || studio.query || "")}` },
            { t: "Kenney", u: "https://kenney.nl/assets?q=2d" },
            { t: "Itch CC0", u: `https://itch.io/game-assets/free/tag-pixel-art?q=${encodeURIComponent(q || "sprite")}` },
          ].map((x) => (
            <a key={x.t} className="gc-chip" href={x.u} target="_blank" rel="noreferrer">{x.t}</a>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="gc-input h-10 px-3 flex-1 min-w-[200px]"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="héroe, caballero, guerrera, slime…"
          />
          <button className="gc-btn gc-btn-primary h-10 px-4" onClick={() => search()} disabled={searching}>
            <MagnifyingGlass size={16} /> {searching ? "Buscando…" : "Buscar"}
          </button>
          <button className="gc-btn gc-btn-ghost h-10 px-4" onClick={askIdeas}>
            <Sparkle size={16} /> Ideas IA
          </button>
          <button className="gc-btn gc-btn-primary h-10 px-4" onClick={generateSprite} disabled={genLoading}>
            <Sparkle size={16} /> {genLoading ? "Generando…" : "Generar sprite"}
          </button>
          <FileDropzone accept="image/*" onFiles={uploadSheet} label="Cortar sheet local" />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {["héroe", "caballero", "guerrera", "slime", "esqueleto", "mago"].map((s) => (
            <button key={s} type="button" className="gc-chip" onClick={() => { setQ(s); setKind("character"); }}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {ideas && <GlassCard className="p-4 whitespace-pre-wrap text-sm leading-relaxed">{ideas}</GlassCard>}
      {!!links.length && (
        <div className="flex flex-wrap gap-2">
          {links.map((l) => (
            <a key={l.url} className="gc-chip" href={l.url} target="_blank" rel="noreferrer">{l.title}</a>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {hits.map((hit) => (
          <GlassCard key={hit.id} className="p-2">
            <div className="aspect-square rounded-xl overflow-hidden flex items-center justify-center" style={{ background: "#0f172a" }}>
              {hit.thumbnail || hit.url ? (
                <img src={hit.thumbnail || hit.url} alt="" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[11px]" style={{ color: "var(--gc-muted)" }}>sin preview</span>
              )}
            </div>
            <div className="text-xs font-semibold mt-2 line-clamp-2">{hit.title}</div>
            <div className="text-[10px]" style={{ color: "var(--gc-muted)" }}>
              {hit.creator} · {hit.license}
              {hit.width ? ` · ${hit.width}×${hit.height}` : ""}
              {hit.source ? ` · ${hit.source}` : ""}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              <button className="gc-chip" onClick={() => saveSprite(hit, "sketch")}>Boceto</button>
              <button className="gc-chip" onClick={() => saveSprite(hit, "sprite")}>
                <FloppyDisk size={11} /> Sprite
              </button>
              <button className="gc-chip" onClick={() => openSlicer(hit)}>
                <Scissors size={11} /> Frames
              </button>
              {hit.foreign_landing_url && (
                <a className="gc-chip" href={hit.foreign_landing_url} target="_blank" rel="noreferrer">
                  <ArrowSquareOut size={11} />
                </a>
              )}
            </div>
          </GlassCard>
        ))}
      </div>

      {sheet && (
        <SheetSlicer
          sheet={sheet}
          studio={studio}
          kind={kind}
          project={project}
          onChange={onChange}
          onClose={() => setSheet(null)}
          onOpenAnim={onOpenAnim}
        />
      )}
    </div>
  );
}

function SheetSlicer({ sheet, studio, kind, project, onChange, onClose, onOpenAnim }) {
  const [cols, setCols] = useState(sheet.cols);
  const [rows, setRows] = useState(sheet.rows);
  const [frames, setFrames] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const previewRef = useRef(null);

  const slice = async () => {
    setBusy(true);
    try {
      const out = await sliceSpriteSheet(sheet.data_url, { cols, rows });
      setFrames(out.frames);
      setIdx(0);
      if (!out.frames.length) toast.info("No se detectaron frames con píxeles. Ajusta columnas/filas.");
    } catch {
      toast.error("No se pudo cortar el sheet");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { slice(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!playing || !frames.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % frames.length), 120);
    return () => clearInterval(t);
  }, [playing, frames.length]);

  const saveAll = async () => {
    if (!frames.length) return;
    const sheetId = crypto.randomUUID();
    const payloads = frames.map((f, i) => ({
      name: `${sheet.name}-f${i + 1}`,
      data_url: f.data_url,
      category: kind || studio.kinds[0].id,
      studio: studio.id,
      width: f.w,
      height: f.h,
      role: "frame",
      sheet_id: sheetId,
      frame_index: i,
      license: sheet.hit.license,
      source: sheet.hit.foreign_landing_url,
      creator: sheet.hit.creator,
    }));
    let sprites = [...(project.sprites || [])];
    for (const payload of payloads) {
      try {
        const saved = await projectsApi.addSprite(project.project_id, payload);
        sprites.push(saved);
      } catch {
        sprites.push({ id: crypto.randomUUID(), ...payload });
      }
    }
    const anim = {
      id: crypto.randomUUID(),
      name: sheet.name,
      fps: 8,
      frames: frames.map((f) => ({ data_url: f.data_url, w: f.w, h: f.h })),
      studio: studio.id,
      source: sheet.hit.foreign_landing_url,
      created_at: new Date().toISOString(),
    };
    onChange({ sprites, animations: [...(project.animations || []), anim] });
    toast.success(`${frames.length} frames guardados`);
    onOpenAnim?.(anim);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)" }}>
      <div className="gc-card p-4 max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold flex items-center gap-2"><FilmStrip size={16} /> Cortar frames</div>
            <div className="text-xs" style={{ color: "var(--gc-muted)" }}>
              {sheet.width}×{sheet.height} · {frames.length} frames · cita {sheet.hit.creator} ({sheet.hit.license})
            </div>
          </div>
          <button className="gc-btn gc-btn-ghost h-8 px-3 text-xs" onClick={onClose}>Cerrar</button>
        </div>
        <div
          className="mt-3 rounded-xl overflow-hidden relative"
          style={{
            background: "#0f172a",
            backgroundImage: `url(${sheet.data_url})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
            minHeight: 180,
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(92,242,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(92,242,255,0.35) 1px, transparent 1px)`,
              backgroundSize: `${100 / cols}% ${100 / rows}%`,
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
          <label>Cols <input type="number" min={1} max={24} className="gc-input h-8 w-16 px-2" value={cols} onChange={(e) => setCols(Math.max(1, Number(e.target.value) || 1))} /></label>
          <label>Filas <input type="number" min={1} max={24} className="gc-input h-8 w-16 px-2" value={rows} onChange={(e) => setRows(Math.max(1, Number(e.target.value) || 1))} /></label>
          <button className="gc-btn gc-btn-ghost h-8 px-3" onClick={slice} disabled={busy}>{busy ? "…" : "Recortar"}</button>
          <button className="gc-btn gc-btn-ghost h-8 px-3" onClick={() => setPlaying((p) => !p)}>
            {playing ? <Pause size={12} /> : <Play size={12} />} Preview
          </button>
        </div>
        <div className="flex gap-1 overflow-x-auto mt-3 pb-1">
          {frames.map((f, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className="shrink-0 w-12 h-12 rounded-lg overflow-hidden border"
              style={{ borderColor: i === idx ? "var(--gc-primary)" : "var(--gc-border)", background: "#0f172a" }}
            >
              <img src={f.data_url} alt="" className="w-full h-full object-contain" style={{ imageRendering: "pixelated" }} />
            </button>
          ))}
        </div>
        {frames[idx] && (
          <div className="mt-3 flex items-center gap-3">
            <img
              ref={previewRef}
              src={frames[idx].data_url}
              alt=""
              className="w-24 h-24 object-contain rounded-xl"
              style={{ imageRendering: "pixelated", background: "#0f172a" }}
            />
            <button className="gc-btn gc-btn-primary h-10 px-4" onClick={saveAll} disabled={!frames.length}>
              Guardar frames y abrir animación
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
